import React, { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  Button,
  Row,
  Col,
  Container,
  Form,
  ListGroup,
  Badge,
  Modal,
  Spinner,
  Alert,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import api from "../api";
import AdminDashboard from "./AdminDashboard";
import AuthContext from "../contexts/AuthContext";

const Home = () => {
  const { auth } = useContext(AuthContext);
  const isAdmin =
    auth.isAdmin ||
    (auth.user && (auth.user.is_staff || auth.user.is_superuser));

  // State for quick worksheet feature
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef();

  // Print status tracking
  const [printStatus, setPrintStatus] = useState(null); // null, 'preparing', 'printing', 'success', 'error'
  const [printError, setPrintError] = useState("");
  const [showPrintStatus, setShowPrintStatus] = useState(false);

  // Fetch available subjects when component mounts
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Fetch questions when subject changes
  useEffect(() => {
    if (selectedSubject) {
      fetchQuestions();
    }
  }, [selectedSubject]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/csv-data/subjects/");
      if (response.data && Array.isArray(response.data)) {
        setSubjects(response.data);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setError("Failed to fetch subjects. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    if (!selectedSubject) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("subject", selectedSubject);
      params.append("limit", 15); // Fetch 15 questions to choose from

      const response = await api.get(
        `/csv-data/worksheet/?${params.toString()}`
      );

      if (response.data && Array.isArray(response.data)) {
        const questionsWithId = response.data.map((q, index) => ({
          ...q,
          tempId: `quick-${index}`, // Add temporary ID for tracking
          selected: false,
        }));

        setAvailableQuestions(questionsWithId);
      } else {
        setError("No questions found for the selected subject.");
        setAvailableQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      setError("Failed to fetch questions. Please try again.");
      setAvailableQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSelect = (tempId) => {
    const question = availableQuestions.find((q) => q.tempId === tempId);

    if (!question) return;

    if (selectedQuestions.includes(tempId)) {
      // Deselect the question
      setSelectedQuestions((prev) => prev.filter((id) => id !== tempId));

      // Update available questions
      setAvailableQuestions((prev) =>
        prev.map((q) => (q.tempId === tempId ? { ...q, selected: false } : q))
      );
    } else {
      // Check if we've already selected 5 questions
      if (selectedQuestions.length >= 5) {
        setError("You can only select up to 5 questions.");
        return;
      }

      // Select the question
      setSelectedQuestions((prev) => [...prev, tempId]);

      // Update available questions
      setAvailableQuestions((prev) =>
        prev.map((q) => (q.tempId === tempId ? { ...q, selected: true } : q))
      );
    }
  };

  // Function to safely render HTML content
  const renderHTML = (htmlContent) => {
    return { __html: htmlContent || "" };
  };

  // Prepare for preview and print
  const prepareForPreview = () => {
    if (selectedQuestions.length === 0) {
      setError("Please select at least one question for your worksheet");
      return;
    }

    setShowPreview(true);
  };

  // Direct browser print implementation
  const directPrint = () => {
    setPrintStatus("printing");
    setShowPrintStatus(true);

    // Create a hidden iframe for printing
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);

    // Get selected questions content
    const selectedQuestionsContent = availableQuestions
      .filter((q) => selectedQuestions.includes(q.tempId))
      .map(
        (question, index) => `
        <div style="margin-bottom: 40px; page-break-after: always;">
          <h4>Question ${index + 1}</h4>
          <div>${question.question_body || ""}</div>
        </div>
      `
      )
      .join("");

    // Get answers content
    const answersContent = availableQuestions
      .filter((q) => selectedQuestions.includes(q.tempId))
      .map(
        (question, index) => `
        <div style="margin-bottom: 30px;">
          <h4>Answer ${index + 1}</h4>
          <div>${question.markscheme_body || ""}</div>
        </div>
      `
      )
      .join("");

    // Create the full HTML document
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Quick Worksheet: ${selectedSubject}</title>
          <style>
            body { 
              font-family: 'Times New Roman', Times, serif; 
              line-height: 1.6;
              margin: 20px;
            }
            .page-break { page-break-before: always; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
            table, th, td { border: 1px solid #ddd; padding: 8px; }
            img { max-width: 100%; height: auto; }
            @media print {
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 30px;">
            <h2>Quick Worksheet: ${selectedSubject}</h2>
          </div>
          
          ${selectedQuestionsContent}
          
          <div class="page-break"></div>
          
          <div style="margin-top: 50px;">
            <h3 style="text-align: center; margin-bottom: 30px;">Answer Key</h3>
            ${answersContent}
          </div>
        </body>
      </html>
    `;

    try {
      // Write the content to the iframe
      const frameDoc = printFrame.contentWindow.document;
      frameDoc.open();
      frameDoc.write(printContent);
      frameDoc.close();

      // Wait for all resources to load then print
      printFrame.onload = () => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          setPrintStatus("success");
          setTimeout(() => setShowPrintStatus(false), 3000);
        } catch (err) {
          console.error("Print window error:", err);
          setPrintStatus("error");
          setPrintError(
            "Error when printing. Try using keyboard shortcut Ctrl+P or Cmd+P instead."
          );
        }
      };

      // Add a cleanup function to remove the frame
      setTimeout(() => {
        try {
          document.body.removeChild(printFrame);
        } catch (e) {
          console.log("Frame already removed");
        }
      }, 5000);
    } catch (error) {
      console.error("Error in direct print:", error);
      setPrintStatus("error");
      setPrintError("Failed to prepare print document. Please try again.");
    }
  };

  // Download as HTML file
  const downloadAsHTML = () => {
    try {
      setPrintStatus("preparing");
      setShowPrintStatus(true);

      // Get selected questions content
      const selectedQuestionsContent = availableQuestions
        .filter((q) => selectedQuestions.includes(q.tempId))
        .map(
          (question, index) => `
          <div style="margin-bottom: 40px; page-break-after: always;">
            <h4>Question ${index + 1}</h4>
            <div>${question.question_body || ""}</div>
          </div>
        `
        )
        .join("");

      // Get answers content
      const answersContent = availableQuestions
        .filter((q) => selectedQuestions.includes(q.tempId))
        .map(
          (question, index) => `
          <div style="margin-bottom: 30px;">
            <h4>Answer ${index + 1}</h4>
            <div>${question.markscheme_body || ""}</div>
          </div>
        `
        )
        .join("");

      // Create the full HTML document
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Quick Worksheet: ${selectedSubject}</title>
            <style>
              body { 
                font-family: 'Times New Roman', Times, serif; 
                line-height: 1.6;
                margin: 20px;
              }
              .page-break { page-break-before: always; }
              table { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
              table, th, td { border: 1px solid #ddd; padding: 8px; }
              img { max-width: 100%; height: auto; }
              @media print {
                .page-break { page-break-before: always; }
              }
            </style>
          </head>
          <body>
            <div style="text-align: center; margin-bottom: 30px;">
              <h2>Quick Worksheet: ${selectedSubject}</h2>
            </div>
            
            ${selectedQuestionsContent}
            
            <div class="page-break"></div>
            
            <div style="margin-top: 50px;">
              <h3 style="text-align: center; margin-bottom: 30px;">Answer Key</h3>
              ${answersContent}
            </div>
          </body>
        </html>
      `;

      // Create a blob and download link
      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Quick_Worksheet_${selectedSubject}.html`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
        setPrintStatus("success");
        setTimeout(() => setShowPrintStatus(false), 3000);
      }, 100);
    } catch (error) {
      console.error("Error creating HTML download:", error);
      setPrintStatus("error");
      setPrintError(
        "Failed to create downloadable worksheet. Please try again."
      );
    }
  };

  return (
    <Container>
      {/* Render Admin Dashboard for admin users */}
      {isAdmin && <AdminDashboard />}

      {/* Print Status Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showPrintStatus}
          onClose={() => setShowPrintStatus(false)}
          bg={
            printStatus === "error"
              ? "danger"
              : printStatus === "success"
              ? "success"
              : "info"
          }
          delay={printStatus === "success" ? 3000 : undefined}
          autohide={printStatus === "success"}
        >
          <Toast.Header>
            <strong className="me-auto">
              {printStatus === "preparing" && "Preparing Worksheet"}
              {printStatus === "printing" && "Printing Worksheet"}
              {printStatus === "success" && "Action Successful"}
              {printStatus === "error" && "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body className={printStatus === "error" ? "text-white" : ""}>
            {printStatus === "preparing" && (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Preparing your worksheet...</span>
              </div>
            )}
            {printStatus === "printing" && (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Sending to printer...</span>
              </div>
            )}
            {printStatus === "success" && "Your worksheet is ready!"}
            {printStatus === "error" && (
              <>
                <div>
                  {printError ||
                    "An error occurred while processing. Please try again."}
                </div>
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Only render the original Home content if not admin */}
      {!isAdmin ? (
        <>
          <div className="p-5 mb-4 bg-light rounded-3">
            <h1>CSV Data Management</h1>
            <p className="lead">
              Upload, view, and manage CSV data with this CRUD application.
            </p>
            <hr className="my-4" />
            <p>
              This application allows you to upload CSV files and perform CRUD
              operations on the data.
            </p>
            <Link to="/upload">
              <Button variant="primary" size="lg">
                Upload a CSV file
              </Button>
            </Link>
          </div>

          <Row className="mt-4">
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header>Upload CSV</Card.Header>
                <Card.Body>
                  <Card.Title>Import your data</Card.Title>
                  <Card.Text>
                    Upload a CSV file to import data into the application. The
                    data will be processed and stored in the database.
                  </Card.Text>
                  <Link to="/upload">
                    <Button variant="outline-primary">Go to Upload</Button>
                  </Link>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header>View Data</Card.Header>
                <Card.Body>
                  <Card.Title>Browse your data</Card.Title>
                  <Card.Text>
                    View all imported data, with options to search, filter,
                    edit, and delete records.
                  </Card.Text>
                  <Link to="/admin-data">
                    <Button variant="outline-primary">View Data</Button>
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : null}

      {/* Quick Worksheet Creator - show for all users */}
      <Card className="mb-4">
        <Card.Header>
          <h4>Quick Worksheet Creator</h4>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          )}

          <p>
            Select a subject and choose up to 5 questions to preview and print.
          </p>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <Form.Select
                  value={selectedSubject}
                  onChange={(e) => {
                    setSelectedSubject(e.target.value);
                    setSelectedQuestions([]);
                  }}
                >
                  <option value="">Select Subject</option>
                  {subjects.map((sub, index) => (
                    <option key={index} value={sub}>
                      {sub}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <div>
                <Badge bg="primary" className="me-2">
                  Selected: {selectedQuestions.length}/5
                </Badge>
                <Button
                  variant="primary"
                  disabled={selectedQuestions.length === 0}
                  onClick={prepareForPreview}
                >
                  Preview & Print
                </Button>
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading questions...</p>
            </div>
          ) : selectedSubject && availableQuestions.length > 0 ? (
            <ListGroup>
              {availableQuestions.map((question) => (
                <ListGroup.Item
                  key={question.tempId}
                  className={`mb-2 ${
                    question.selected ? "border-primary" : ""
                  }`}
                  action
                  onClick={() => handleQuestionSelect(question.tempId)}
                >
                  <div className="d-flex align-items-start">
                    <Form.Check
                      type="checkbox"
                      checked={selectedQuestions.includes(question.tempId)}
                      onChange={() => {}}
                      className="me-2 mt-1"
                    />
                    <div>
                      <div className="mb-2">
                        <strong>{question.title || "Question"}</strong>
                        {question.selected && (
                          <Badge bg="primary" className="ms-2">
                            Selected
                          </Badge>
                        )}
                      </div>
                      <div
                        className="question-preview html-content-view"
                        dangerouslySetInnerHTML={renderHTML(
                          question.question_body
                        )}
                      />
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : selectedSubject ? (
            <Alert variant="info">
              No questions found for this subject. Please try another subject.
            </Alert>
          ) : (
            <Alert variant="info">
              Please select a subject to view available questions.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Quick Worksheet Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={printRef} className="p-4">
            <div className="text-center mb-4">
              <h2>Quick Worksheet: {selectedSubject}</h2>
            </div>

            {availableQuestions
              .filter((q) => selectedQuestions.includes(q.tempId))
              .map((question, index) => (
                <div key={question.tempId} className="mb-5">
                  <h4>Question {index + 1}</h4>
                  <div
                    className="question-text html-content-view mb-3"
                    dangerouslySetInnerHTML={renderHTML(question.question_body)}
                  />
                  <div className="page-break-after"></div>
                </div>
              ))}

            {/* Answer section - will only show during print */}
            <div className="mt-5 answer-section">
              <h3 className="text-center mb-4">Answer Key</h3>
              {availableQuestions
                .filter((q) => selectedQuestions.includes(q.tempId))
                .map((question, index) => (
                  <div key={`answer-${question.tempId}`} className="mb-4">
                    <h4>Answer {index + 1}</h4>
                    <div
                      className="answer-text html-content-view"
                      dangerouslySetInnerHTML={renderHTML(
                        question.markscheme_body
                      )}
                    />
                  </div>
                ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
          <div className="ms-auto">
            <Button
              variant="primary"
              onClick={directPrint}
              className="me-2"
              disabled={
                printStatus === "preparing" || printStatus === "printing"
              }
            >
              {printStatus === "preparing" || printStatus === "printing" ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">
                    {printStatus === "preparing"
                      ? "Preparing..."
                      : "Printing..."}
                  </span>
                </>
              ) : (
                "Print Worksheet"
              )}
            </Button>
            <Button variant="outline-primary" onClick={downloadAsHTML}>
              Download as HTML
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Home;
